import { useRef, useEffect } from 'react';
import { Icon } from '@/components/Icon'
import { useFormField, FormDisabled } from '@/form/FormProvider';

// This component has special "required" behavior: If required, it must be checked.
// When no longer required, the error is cleared.

export function CheckboxSection({ title, text, ariaLabel, hint, children, ...args }) {

  const hasInteracted = useRef(false);
  const sectionRef = useRef(null);

  const { name, value, setValue, error, setError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {
      if (required && !value) {
        return required;
      }
    },
    formatValue: (value, pretty) => value || false,
    parseValue: (value) => value || false,
    getFocusElement: ({name, parent}) => parent.querySelector(`.checkbox_option[name="${name}"]`),
    ...args
  });

  useEffect(() => {
    if (!required) {
      setError(null);
    }
  }, [required, setError]);

  function handleChange(newvalue) {
    hasInteracted.current = true;

    if (newvalue && sectionRef.current) {
      const scroller = sectionRef.current.closest('.nearest_scrollable_container');
      if (scroller) {
        const sectionBottomInContent = scroller.scrollTop + sectionRef.current.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top;
        const nearContentBottom = scroller.scrollHeight - sectionBottomInContent < 20;
        const start = performance.now();
        const follow = () => {
          const sectionRect = sectionRef.current.getBoundingClientRect();
          const scrollerRect = scroller.getBoundingClientRect();
          if (nearContentBottom) {
            scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight;
          } else {
            const overflow = sectionRect.bottom - scrollerRect.bottom + 90;
            if (overflow > 0) {
              const roomAbove = sectionRect.top - scrollerRect.top - 6;
              if (roomAbove > 0) scroller.scrollTop += Math.min(overflow, roomAbove);
            }
          }
          if (performance.now() - start < 350) requestAnimationFrame(follow);
        };
        requestAnimationFrame(follow);
      }
    }

    setValue(newvalue);
    setError(null);
  }

  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <div ref={sectionRef} className={"checkbox_section" + (value ? " expanded" : "") + (hasInteracted.current ? "" : " no-animate")}>
        <div 
          className={"checkbox_option" + (value ? " checked" : "")} 
          tabIndex="0" 
          name={name}
          onClick={() => handleChange(!value)} 
          role="checkbox" 
          aria-label={ariaLabel || text} 
          aria-checked={value ? "true" : "false"}
          inert={(disabled || obviated) ? "true" : undefined}
        >
          <div className="checkbox">
            <Icon name={value ? "checkbox-marked" : "checkbox-blank-outline"}/>
          </div>
          <div className="label">{text}</div>
        </div>
        <div className="checkbox_content" onTransitionEnd={(e) => { if (e.target === e.currentTarget) window.dispatchEvent(new Event('resize')); }}>
          <div>
            <div>
              <FormDisabled obviated={!value}>
                {children}
              </FormDisabled>
            </div>
          </div>
        </div>
      </div>
      {(error && !(disabled || obviated || readOnly)) ? <div className="input_error">{error}</div> : (hint ? <div className="input_hint">{hint}</div> : null)}
    </>
  )
}